// #1920 — decides WHEN a waiting service worker is safe to activate without
// a scribe's explicit consent.
//
// The scribe kiosk rule (docs/score digest: "KIOSKS, no escape hatches") cuts
// both ways here: a scoring session must never be yanked out from under a
// scribe mid-ride, but relying on a dismissible snackbar (the #1920 bug) means
// a scribe who dismisses or never sees it keeps running an old, precached
// shell indefinitely, one whose bundle hashes a later deploy has already
// removed from the CDN, surfacing as "Failed to fetch" (bug #183).
//
// This is an explicit SAFE-list, not a deny-list of one known-bad route: a
// new data-entry route added later defaults to unsafe until someone puts it
// on the list on purpose, instead of silently inheriting "safe" the way a
// deny-list would. Two routes were wrongly certified safe in an earlier
// version of this file and are now deliberately excluded:
//
// - /classes/:classId/grid (ClassGridRow.jsx) holds per-keystroke, uncommitted
//   draft state (`obsDrafts`, `totalDraft`, `placeDraft`) that only reaches
//   the debounced autosave outbox on blur. A reload mid-cell-edit silently
//   drops whatever the scribe was typing, exactly the "yanked out mid-ride"
//   hazard AC3 forbids, on a route this file's own comment used to certify
//   safe.
// - /callback is the OIDC redirect target while CallbackPage is exchanging
//   the authorization code. Reloading there re-submits an already-consumed
//   `?code=` to the IdP, which comes back as an auth error; common-fe's
//   AuthGate treats an auth error as fatal full-screen whenever the session
//   is not yet authenticated (#1289). Auto-reloading a login callback to fix
//   one dead end risks producing a different one.
//
// The scoring sheet (/rides/:rideId/score) is unsafe for the same reason as
// the grid: per-observation entry state, addressed since #295/#1920.
//
// Safe: routes that only ever render from server-fetched data with no local
// draft state, where a reload is no different from the full-page reload
// sign-out already performs (/, /setup, /classes, /classes/:classId ride
// list, /logout, /logged-out).
//
// /setup is the deliberate judgement call on that list, not an oversight:
// InitialSetup.jsx holds a `useState("")` showId plus an org Select, so an
// auto-applied update there discards a half-made selection. That is two
// dropdowns on a pre-scoring screen, re-picked in seconds, versus leaving a
// scribe on a stale shell right up until they start scoring — which is the
// worst possible moment to discover the bundle is gone. Staying stale is the
// bigger harm here, so /setup is on the list on purpose. (/classes is the
// adjacent hazard and is genuinely clean: unsynced rides live in IndexedDB
// via src/local/db.js, so a reload there drops nothing.)
const SAFE_ROUTES = [
  /^\/$/,
  /^\/setup$/,
  /^\/classes$/,
  /^\/classes\/[^/]+$/,
  /^\/logout$/,
  /^\/logged-out$/,
];

export function isSafeRouteForUpdate(pathname) {
  return SAFE_ROUTES.some((re) => re.test(pathname));
}

// Whether a waiting service worker should be activated (and the page
// reloaded) right now, given the current route. Safe only on the explicit
// list above; every other route, known or future, defaults to unsafe.
export function shouldAutoApplyUpdate(pathname) {
  return isSafeRouteForUpdate(pathname);
}
